/**
 * OmegaKillSwitchPage — `/omega/kill-switch`.
 *
 * Status surface for the Constitution C-4 / Protocol §XVI.4 kill
 * switch. Quorum is 3-of-5 successor signatures. The "Initiate Kill"
 * button is disabled until the 5 quorum members are onboarded — the
 * engine refuses to accept a kill signature from an unregistered
 * key, so the UI gate mirrors that.
 *
 * Kill switch status is read from `GET /api/v1/omega/state` which
 * returns `kill_switch: { initiated, collected, required, last_drill_iso }`.
 * The founder direct-kill endpoint is `POST /api/v1/admin/omega-kill`.
 */

import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { cn } from '@/utils/cn';
import { apiFetch } from '@/api/http';

const QUORUM_TOTAL = 5;

interface KillSwitchStatus {
  initiated: boolean;
  collected: number;
  required: number;
  last_drill_iso: string | null;
}

interface OmegaStateKill {
  kill_switch: KillSwitchStatus;
  alive: boolean;
}

interface QuorumMember {
  slot: number;
  display_name: string;
  status: 'onboarded' | 'pending';
}

// Phase 1: no quorum-member registry exists on the server yet.
// These slots are placeholders until a /api/v1/admin/omega/kill-quorum endpoint ships.
const QUORUM_ROSTER: QuorumMember[] = [
  { slot: 1, display_name: 'Successor seat 1', status: 'pending' },
  { slot: 2, display_name: 'Successor seat 2', status: 'pending' },
  { slot: 3, display_name: 'Successor seat 3', status: 'pending' },
  { slot: 4, display_name: 'Successor seat 4', status: 'pending' },
  { slot: 5, display_name: 'Successor seat 5', status: 'pending' },
];

export function OmegaKillSwitchPage() {
  if (!isOmegaAdmin()) {
    return <Navigate to="/" replace />;
  }
  return <KillSwitchInner />;
}

function KillSwitchInner() {
  const { data, isPending, isError } = useQuery<OmegaStateKill>({
    queryKey: ['omega', 'kill-switch', 'status'],
    queryFn: () => apiFetch<OmegaStateKill>('/omega/state'),
    refetchInterval: 15_000,
    staleTime: 10_000,
  });

  const ks = data?.kill_switch;
  const isAlive = data?.alive ?? true;
  const initiated = ks?.initiated ?? false;
  const collected = ks?.collected ?? 0;
  const required = ks?.required ?? 3;
  const killed = !isAlive;

  const onboardedCount = QUORUM_ROSTER.filter((m) => m.status === 'onboarded').length;
  const quorumReady = onboardedCount >= required;

  let engineState: 'alive' | 'killed' | 'in-progress' = 'alive';
  if (killed) engineState = 'killed';
  else if (initiated) engineState = 'in-progress';

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb label="Kill Switch" />

      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">
          Kill Switch
        </h1>
        <p className="text-sm text-ink-muted">
          Constitution C-4. Halts OMEGA on {required}-of-{QUORUM_TOTAL}{' '}
          valid signatures from the named successor quorum. Irreversible
          for the lifetime of this engine.
        </p>
      </header>

      {isError && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger">
          Failed to load kill switch state from server.
        </div>
      )}

      <section
        aria-labelledby="kill-status-heading"
        className={cn(
          'rounded-xl border-2 p-6',
          killed
            ? 'border-danger/50 bg-danger/5'
            : initiated
              ? 'border-warning/50 bg-warning/5'
              : 'border-success/30 bg-surface',
        )}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2
              id="kill-status-heading"
              className="text-lg font-semibold text-ink"
            >
              Current status
            </h2>
            <p className="mt-1 text-sm text-ink-muted">
              {isPending
                ? 'Loading engine state…'
                : killed
                  ? 'OMEGA has been halted. This is irreversible.'
                  : initiated
                    ? `Kill round in progress — ${collected} of ${required} signatures collected.`
                    : 'The engine is operational. No kill round is currently in progress.'}
            </p>
          </div>
          <StatusPill state={engineState} />
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field
            label="Quorum requirement"
            value={`${required} of ${QUORUM_TOTAL}`}
          />
          <Field
            label="Quorum onboarded"
            value={`${onboardedCount} of ${QUORUM_TOTAL}`}
          />
          <Field
            label="Active kill round"
            value={initiated ? `${collected}/${required} collected` : 'None'}
          />
        </div>

        {ks?.last_drill_iso && (
          <div className="mt-3 text-xs text-ink-muted">
            Last drill: {new Date(ks.last_drill_iso).toLocaleString()}
          </div>
        )}
      </section>

      <section
        aria-labelledby="kill-roster-heading"
        className="rounded-xl border border-border bg-surface p-6"
      >
        <h2
          id="kill-roster-heading"
          className="text-lg font-semibold text-ink"
        >
          Successor quorum roster
        </h2>
        <p className="mt-1 text-sm text-ink-muted">
          Each seat must complete a key ceremony and sign a
          QuorumMemberRegistered entry on the log before the kill switch
          becomes initiable.
        </p>
        <ul className="mt-5 flex flex-col gap-2">
          {QUORUM_ROSTER.map((m) => (
            <li
              key={m.slot}
              className="flex items-center justify-between gap-3 rounded-lg border border-border bg-canvas px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs uppercase tracking-wide text-ink-subtle">
                  Seat {m.slot}
                </span>
                <span className="text-sm text-ink">{m.display_name}</span>
              </div>
              <RosterBadge status={m.status} />
            </li>
          ))}
        </ul>
      </section>

      <section
        aria-labelledby="kill-action-heading"
        className="rounded-xl border-2 border-danger/30 bg-surface p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2
              id="kill-action-heading"
              className="text-lg font-semibold text-ink"
            >
              Initiate kill round
            </h2>
            <p className="mt-1 text-sm text-ink-muted">
              Opens a quorum collection window. Halts OMEGA when{' '}
              {required} valid signatures land. This action does
              NOT halt OMEGA on its own.
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-md border border-danger/40 bg-danger/5 p-4 text-sm">
          <p className="font-semibold uppercase tracking-wide text-danger">
            Irreversible
          </p>
          <p className="mt-1 text-ink-muted">
            Once {required} valid signatures land on the log,
            OMEGA halts permanently for the lifetime of this engine. No
            signature, in any quorum, by any party, restores it. This
            page does not bypass the quorum — it only opens the round.
          </p>
        </div>

        <div className="mt-5 flex items-center justify-end gap-3">
          {!quorumReady ? (
            <span className="text-sm text-ink-muted">
              Disabled until {required} of {QUORUM_TOTAL} successor
              members are onboarded.
            </span>
          ) : null}
          <button
            type="button"
            disabled
            aria-disabled
            data-testid="omega-kill-initiate"
            className={cn(
              'inline-flex items-center justify-center rounded-md px-4 py-2',
              'text-sm font-semibold transition-colors',
              'border border-danger/40 bg-danger/10 text-danger',
              'cursor-not-allowed opacity-50',
            )}
            title={
              quorumReady
                ? 'Open kill round'
                : 'Waiting on successor quorum onboarding'
            }
          >
            Initiate Kill
          </button>
        </div>
      </section>
    </div>
  );
}

function StatusPill({ state }: { state: 'alive' | 'killed' | 'in-progress' }) {
  const styles: Record<string, string> = {
    alive: 'border-success/30 bg-success-soft text-success',
    killed: 'border-danger/40 bg-danger/10 text-danger',
    'in-progress': 'border-warning/40 bg-warning/10 text-warning',
  };
  const dots: Record<string, string> = {
    alive: 'bg-success',
    killed: 'bg-danger',
    'in-progress': 'bg-warning',
  };
  const labels: Record<string, string> = {
    alive: 'Alive',
    killed: 'Killed',
    'in-progress': 'Kill Round Active',
  };

  return (
    <span
      role="status"
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1',
        'text-xs font-semibold uppercase tracking-wide',
        styles[state],
      )}
    >
      <span
        aria-hidden
        className={cn('h-1.5 w-1.5 rounded-full', dots[state])}
      />
      {labels[state]}
    </span>
  );
}

function RosterBadge({ status }: { status: 'onboarded' | 'pending' }) {
  const onboarded = status === 'onboarded';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5',
        'text-[10px] font-semibold uppercase tracking-wide',
        onboarded
          ? 'border-success/30 bg-success-soft text-success'
          : 'border-border bg-canvas text-ink-subtle',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          onboarded ? 'bg-success' : 'bg-ink-subtle',
        )}
      />
      {status}
    </span>
  );
}

function Breadcrumb({ label }: { label: string }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm">
      <Link
        to="/omega"
        className="text-ink-muted transition-colors hover:text-ink"
      >
        &larr; OMEGA
      </Link>
      <span className="text-ink-subtle">/</span>
      <span className="text-ink">{label}</span>
    </nav>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border bg-canvas px-3 py-3">
      <span className="text-[10px] uppercase tracking-wider text-ink-subtle">
        {label}
      </span>
      <span className="text-sm font-semibold text-ink">{value}</span>
    </div>
  );
}
